import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';

import { SUPPORT_CONTACT } from './supportContact';

export { SUPPORT_CONTACT };

export const SUPPORT_GRADIENT = 'linear-gradient(135deg, #0369a1 0%, #0284c7 55%, #0891b2 100%)';

const Panel = styled('div')(() => ({
  position: 'fixed',
  right: 24,
  bottom: 99,
  width: 340,
  maxWidth: 'calc(100vw - 32px)',
  zIndex: 9999,
  borderRadius: 16,
  overflow: 'hidden',
  fontFamily: "'Inter Tight', Inter, -apple-system, 'Segoe UI', sans-serif",
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  boxShadow: '0 24px 48px -16px rgba(15, 23, 42, 0.35), 0 4px 12px rgba(15, 23, 42, 0.08)',
  animation: 'supportIn 0.18s ease-out',
  '@keyframes supportIn': {
    from: { opacity: 0, transform: 'translateY(8px) scale(0.98)' },
    to: { opacity: 1, transform: 'none' }
  },
  '& .sp-head': {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '16px 14px 16px 18px',
    color: '#ffffff',
    background: SUPPORT_GRADIENT
  },
  '& .sp-head-text': { flex: 1, minWidth: 0, lineHeight: 1.3 },
  '& .sp-head strong': { display: 'block', fontSize: 15, fontWeight: 700 },
  '& .sp-head span': { fontSize: 12, opacity: 0.85 },
  '& .sp-close': {
    width: 30,
    height: 30,
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    color: '#ffffff',
    background: 'transparent',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': { background: 'rgba(255, 255, 255, 0.16)' }
  },
  '& .sp-body': { padding: 16 },
  '& .sp-contact': {
    padding: '12px 14px',
    borderRadius: 12,
    border: '1px solid var(--border)',
    background: 'var(--surface-2)'
  },
  '& .sp-contact-label': {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--subtle)'
  },
  '& .sp-contact a': {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 6,
    textDecoration: 'none',
    color: '#0284c7'
  },
  '& .sp-contact a:hover': { color: '#0369a1' },
  '& .sp-phone': {
    fontFamily: 'var(--font-mono, ui-monospace, Menlo, Consolas, monospace)',
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: '0.02em'
  },
  '& .sp-email': { fontSize: 13.5, fontWeight: 600 },
  '& .sp-label': {
    display: 'block',
    margin: '16px 0 8px',
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--text-2)'
  },
  '& textarea': {
    width: '100%',
    minHeight: 72,
    resize: 'vertical',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid var(--border)',
    background: 'var(--surface)',
    color: 'var(--text)',
    font: 'inherit',
    fontSize: 13.5,
    outline: 'none',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    '&::placeholder': { color: 'var(--subtle)' },
    '&:focus': {
      borderColor: '#0284c7',
      boxShadow: '0 0 0 3px rgba(2, 132, 199, 0.15)'
    }
  },
  '& .sp-submit': {
    width: '100%',
    height: 42,
    marginTop: 12,
    border: 'none',
    borderRadius: 10,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    font: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    color: '#ffffff',
    background: SUPPORT_GRADIENT,
    boxShadow: '0 6px 16px -8px rgba(2, 132, 199, 0.7)',
    transition: 'filter 0.15s ease, transform 0.15s ease',
    '&:hover:not(:disabled)': { filter: 'brightness(1.06)', transform: 'translateY(-1px)' },
    '&:disabled': { cursor: 'not-allowed', opacity: 0.55, boxShadow: 'none' }
  },
  '& .sp-assistant': {
    display: 'block',
    width: '100%',
    marginTop: 10,
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    font: 'inherit',
    fontSize: 12.5,
    fontWeight: 500,
    color: 'var(--muted)',
    textAlign: 'center',
    '&:hover': { color: '#0284c7' }
  }
}));

const Svg = ({ children, size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    {children}
  </svg>
);

// Technical-support popup: hotline/email plus a quick incident ticket.
// Tickets are handed to the assistant chat (no ticket API on the backend yet).
const SupportPanel = ({ onClose, onSubmitTicket, onOpenAssistant }) => {
  const [ticket, setTicket] = useState('');
  const textareaRef = useRef(null);

  useEffect(() => {
    textareaRef.current?.focus();
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = () => {
    const text = ticket.trim();
    if (!text) return;
    onSubmitTicket(text);
    setTicket('');
  };

  return (
    <Panel className="support-fab" role="dialog" aria-label="Hỗ trợ kỹ thuật">
      <div className="sp-head">
        <Svg size={22}>
          <path d="M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm0 0a9 9 0 1 1 18 0m0 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3Z" />
          <path d="M21 16v2a4 4 0 0 1-4 4h-5" />
        </Svg>
        <div className="sp-head-text">
          <strong>Hỗ trợ kỹ thuật GBS - CNHP</strong>
          <span>Đội ngũ kỹ sư IoT hỗ trợ vận hành hệ thống</span>
        </div>
        <button type="button" className="sp-close" aria-label="Đóng" onClick={onClose}>
          <Svg size={18}>
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </Svg>
        </button>
      </div>

      <div className="sp-body">
        <div className="sp-contact">
          <div className="sp-contact-label">Hotline hỗ trợ kỹ thuật</div>
          <a className="sp-phone" href={`tel:${SUPPORT_CONTACT.hotlineTel}`}>
            <Svg size={18}>
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </Svg>
            {SUPPORT_CONTACT.hotline}
          </a>
          <a className="sp-email" href={`mailto:${SUPPORT_CONTACT.email}`}>
            <Svg size={16}>
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            </Svg>
            {SUPPORT_CONTACT.email}
          </a>
        </div>

        <label className="sp-label" htmlFor="support-ticket">
          Báo cáo sự cố mạng lưới / truyền tin:
        </label>
        <textarea
          id="support-ticket"
          ref={textareaRef}
          value={ticket}
          onChange={(e) => setTicket(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit();
          }}
          placeholder="Nhập mã ĐBDD hoặc mô tả sự cố rò rỉ, mất sóng..."
          maxLength={1000}
        />
        <button type="button" className="sp-submit" onClick={submit} disabled={!ticket.trim()}>
          <Svg size={16}>
            <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
            <path d="m21.854 2.147-10.94 10.939" />
          </Svg>
          Gửi phiếu xử lý sự cố
        </button>
        <button type="button" className="sp-assistant" onClick={onOpenAssistant}>
          Hoặc mở trợ lý AI để tra cứu dữ liệu →
        </button>
      </div>
    </Panel>
  );
};

export default SupportPanel;
